import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X, MapPinned, ClipboardCheck, CalendarClock } from "lucide-react";

const DIAS = [
  { v: 0, label: "Segunda-feira", short: "SEG" },
  { v: 1, label: "Terça-feira", short: "TER" },
  { v: 2, label: "Quarta-feira", short: "QUA" },
  { v: 3, label: "Quinta-feira", short: "QUI" },
  { v: 4, label: "Sexta-feira", short: "SEX" },
  { v: 5, label: "Sábado", short: "SAB" },
  { v: 6, label: "Domingo", short: "DOM" },
];

export default function Rotas() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ nome: "", dia_semana: 3 });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ nome: "", dia_semana: 3 });
  const [limite, setLimite] = useState(0);
  const [limiteSaving, setLimiteSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/rotas");
      setRows(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/business/settings").then((r) => setLimite(r.data.limite_remarcacoes || 0)).catch(() => {});
  }, []);

  const salvarLimite = async () => {
    setLimiteSaving(true);
    try {
      await api.put("/business/settings", { limite_remarcacoes: Math.max(0, parseInt(limite || 0)) });
      toast.success(limite > 0 ? `Limite salvo: ${limite} remarcações por cliente` : "Limite removido — remarcações ilimitadas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally {
      setLimiteSaving(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return toast.error("Dê um nome para a rota");
    try {
      await api.post("/rotas", { nome: form.nome.trim(), dia_semana: Number(form.dia_semana) });
      toast.success("Rota criada");
      setShowNew(false);
      setForm({ nome: "", dia_semana: 3 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditForm({ nome: r.nome, dia_semana: r.dia_semana });
  };

  const saveEdit = async () => {
    if (!editForm.nome.trim()) return toast.error("Dê um nome para a rota");
    try {
      await api.put(`/rotas/${editingId}`, { nome: editForm.nome.trim(), dia_semana: Number(editForm.dia_semana) });
      toast.success("Rota atualizada");
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const removeRota = async (r) => {
    if (!window.confirm(`Excluir a rota "${r.nome}"? Os clientes vinculados ficam sem rota.`)) return;
    try {
      await api.delete(`/rotas/${r.id}`);
      toast.success("Rota excluída");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  return (
    <div>
      <header className="mb-5">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-stone-900">Rotas de Cobrança</h1>
        <p className="text-sm text-stone-500 mt-1">
          Defina o dia da semana de cada rota. As parcelas dos clientes dessa rota serão agendadas
          automaticamente para o dia certo da cobrança.
        </p>
      </header>

      <div className="carne-card p-4 mb-4 bg-amber-50 border-amber-200 text-xs text-amber-900 leading-relaxed" data-testid="rotas-regra-info">
        <div className="font-bold text-amber-900 mb-1">Como funciona</div>
        O dia da rota + os 3 dias seguintes caem na rota atual. Os outros 3 dias caem na próxima
        ocorrência. Exemplo: rota <b>Quinta</b> — vencimento numa quinta/sexta/sábado/domingo é cobrado nessa quinta;
        se vencer segunda/terça/quarta vai pra próxima quinta.
      </div>

      <div className="flex justify-end mb-3">
        <button
          data-testid="btn-add-rota"
          onClick={() => setShowNew(!showNew)}
          className="btn-primary text-sm py-2 px-3"
        >
          <Plus className="w-4 h-4" /> Nova rota
        </button>
      </div>

      {showNew && (
        <form onSubmit={save} className="carne-card p-4 mb-4 animate-slide-up space-y-3" data-testid="new-rota-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Nome da rota*</label>
              <input
                data-testid="input-rota-nome"
                placeholder="Ex: Centro, Bairro Alto, Rota de 5ª"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Dia da semana*</label>
              <select
                data-testid="select-rota-dia"
                value={form.dia_semana}
                onChange={(e) => setForm({ ...form, dia_semana: e.target.value })}
                className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
              >
                {DIAS.map((d) => (
                  <option key={d.v} value={d.v}>{d.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => { setShowNew(false); setForm({ nome: "", dia_semana: 3 }); }} className="h-12 px-4 rounded-xl font-bold text-stone-600 hover:text-stone-900">
              Cancelar
            </button>
            <button data-testid="btn-save-rota" type="submit" className="btn-primary flex-1">
              <Save className="w-4 h-4" /> Salvar rota
            </button>
          </div>
        </form>
      )}

      {loading && <div className="text-stone-500 text-sm">Carregando…</div>}

      <div className="space-y-3" data-testid="rota-list-container">
        {rows.map((r) => (
          <div key={r.id} className="carne-card p-4 flex items-start gap-3" data-testid={`rota-card-${r.id}`}>
            <div className="w-11 h-11 rounded-full bg-[color:var(--terracotta-light)] text-[color:var(--terracotta)] flex items-center justify-center font-display text-sm font-bold shrink-0">
              {DIAS.find((d) => d.v === r.dia_semana)?.short || "?"}
            </div>
            <div className="flex-1 min-w-0">
              {editingId === r.id ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    data-testid={`edit-rota-nome-${r.id}`}
                    value={editForm.nome}
                    onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                    className="h-11 px-3 rounded-lg border border-stone-200 bg-white"
                  />
                  <select
                    data-testid={`edit-rota-dia-${r.id}`}
                    value={editForm.dia_semana}
                    onChange={(e) => setEditForm({ ...editForm, dia_semana: e.target.value })}
                    className="h-11 px-3 rounded-lg border border-stone-200 bg-white"
                  >
                    {DIAS.map((d) => (
                      <option key={d.v} value={d.v}>{d.label}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div className="font-display text-base font-bold text-stone-900 truncate">{r.nome}</div>
                  <div className="text-xs text-stone-500 mt-0.5 inline-flex items-center gap-1">
                    <MapPinned className="w-3 h-3" />
                    {DIAS.find((d) => d.v === r.dia_semana)?.label || "—"}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {editingId === r.id ? (
                <>
                  <button data-testid={`btn-save-rota-${r.id}`} onClick={saveEdit} className="h-9 px-3 rounded-full bg-[color:var(--terracotta)] text-white text-xs font-bold inline-flex items-center gap-1">
                    <Save className="w-3.5 h-3.5" /> Salvar
                  </button>
                  <button onClick={() => setEditingId(null)} className="h-9 w-9 rounded-full text-stone-500 hover:text-stone-900 inline-flex items-center justify-center">
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to={`/rotas/${r.id}/conferencia`}
                    data-testid={`btn-conf-rota-${r.id}`}
                    className="h-9 px-3 rounded-full bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1 hover:bg-emerald-700"
                    title="Conferência de rota"
                  >
                    <ClipboardCheck className="w-3.5 h-3.5" /> Conferir
                  </Link>
                  <button data-testid={`btn-edit-rota-${r.id}`} onClick={() => startEdit(r)} className="h-9 w-9 rounded-full text-stone-500 hover:bg-stone-100 inline-flex items-center justify-center">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`btn-del-rota-${r.id}`} onClick={() => removeRota(r)} className="h-9 w-9 rounded-full text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {!loading && rows.length === 0 && (
          <div className="text-center py-10 text-stone-500 text-sm">
            Nenhuma rota ainda. <button onClick={() => setShowNew(true)} className="text-[color:var(--terracotta)] font-bold">Criar a primeira</button>
          </div>
        )}
      </div>

      <section className="carne-card p-4 mt-6" data-testid="limite-remarcacoes-section">
        <h2 className="font-display text-base font-bold text-stone-900 mb-2 inline-flex items-center gap-1">
          <CalendarClock className="w-4 h-4 text-amber-700" /> Limite de remarcações por cliente
        </h2>
        <p className="text-xs text-stone-500 mb-3">
          Quantas vezes um mesmo cliente pode ter cobranças remarcadas antes de ser bloqueado. Deixe <b>0</b> para ilimitado.
        </p>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Limite (0 = ilimitado)</label>
            <input
              data-testid="input-limite-remarcacoes"
              type="number"
              min="0"
              max="999"
              value={limite}
              onChange={(e) => setLimite(e.target.value)}
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white font-mono-num"
            />
          </div>
          <button
            data-testid="btn-salvar-limite"
            onClick={salvarLimite}
            disabled={limiteSaving}
            className="btn-primary h-12 px-4"
          >
            <Save className="w-4 h-4" /> Salvar
          </button>
        </div>
      </section>
    </div>
  );
}
